import React, { useMemo, useState } from "react";
import { filtrerSites, libelleCompteSites, sitesDeOrganisation } from "./utilisateurHelpers";

// ============================================================
// OrganisationsEtSites — rattachements d'un utilisateur : organisations en
// cartes à cocher, puis les sites des organisations cochées, regroupés par
// organisation (repliables, « tout sélectionner », filtre par nom quand la
// liste s'allonge). Composant contrôlé : la sélection vit dans le parent.
// ============================================================

export const SEUIL_FILTRE = 8;

const OrganisationsEtSites = ({
  organizations = [],
  sites = [],
  selectionOrganisations = [],
  selectionSites = [],
  organisationsEnChargement = [],
  onBasculerOrganisation,
  onBasculerSite,
  onDefinirSitesDe,
  blocage = null,
  onFermerBlocage = () => {},
  erreurs = {},
  idPrefix = "rattachements",
}) => {
  const [filtre, setFiltre] = useState("");
  const [replies, setReplies] = useState([]);

  const organisationsCochees = useMemo(
    () => organizations.filter((o) => selectionOrganisations.includes(o.id)),
    [organizations, selectionOrganisations]
  );
  const totalSites = useMemo(
    () => organisationsCochees.reduce((n, o) => n + sitesDeOrganisation(sites, o.id).length, 0),
    [organisationsCochees, sites]
  );
  const afficherFiltre = totalSites >= SEUIL_FILTRE;
  const filtreActif = filtre.trim().length > 0;

  const basculerRepli = (organisationId) =>
    setReplies((r) => (r.includes(organisationId) ? r.filter((o) => o !== organisationId) : [...r, organisationId]));

  const compteSelectionnes = (organisationId) =>
    sitesDeOrganisation(sites, organisationId).filter((s) => selectionSites.includes(s.id)).length;

  return (
    <div className="orga-sites">
      <fieldset className="orga-sites__bloc">
        <legend className="orga-sites__legende">
          Organisations
          <span className="orga-sites__compteur">{selectionOrganisations.length}</span>
        </legend>
        {organizations.length === 0 ? (
          <p className="orga-sites__vide">Chargement des organisations…</p>
        ) : (
          <ul className="orga-sites__grille">
            {organizations.map((organisation) => {
              const coche = selectionOrganisations.includes(organisation.id);
              const id = `${idPrefix}-orga-${organisation.id}`;
              const nbSites = sitesDeOrganisation(sites, organisation.id).length;
              return (
                <li key={organisation.id}>
                  <label htmlFor={id} className={`choix-carte${coche ? " is-checked" : ""}`}>
                    <input
                      type="checkbox"
                      id={id}
                      name="organizations"
                      value={organisation.id}
                      checked={coche}
                      onChange={() => onBasculerOrganisation(organisation.id)}
                    />
                    <span className="choix-carte__texte">
                      <span className="choix-carte__nom">{organisation.name}</span>
                      {coche && (
                        <span className="choix-carte__detail">
                          {organisationsEnChargement.includes(organisation.id)
                            ? "Sites en cours de chargement…"
                            : libelleCompteSites(compteSelectionnes(organisation.id), nbSites)}
                        </span>
                      )}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
        {erreurs.organizations && (
          <small role="alert" className="form-text text-danger">
            {erreurs.organizations}
          </small>
        )}
      </fieldset>

      {blocage && (
        <div className="orga-sites__blocage" role="alert">
          <i className="fa fa-exclamation-circle" aria-hidden="true" />
          <span>
            Décochez d'abord {blocage.sites.length > 1 ? "les sites" : "le site"}{" "}
            <b>{blocage.sites.join(", ")}</b> avant de retirer l'organisation{" "}
            <b>{blocage.organisation.name}</b>.
          </span>
          <button type="button" className="btn btn-sm btn-link" onClick={onFermerBlocage}>
            Compris
          </button>
        </div>
      )}

      <div className="orga-sites__bloc">
        <div className="orga-sites__barre">
          <h3 className="orga-sites__legende" id={`${idPrefix}-sites-titre`}>
            Sites
            <span className="orga-sites__compteur">{selectionSites.length}</span>
          </h3>
          {afficherFiltre && (
            <div className="orga-sites__filtre">
              <label htmlFor={`${idPrefix}-filtre`} className="sr-only">
                Filtrer les sites par nom ou commune
              </label>
              <i className="fa fa-search" aria-hidden="true" />
              <input
                id={`${idPrefix}-filtre`}
                type="search"
                className="form-control"
                placeholder="Filtrer les sites par nom ou commune…"
                value={filtre}
                onChange={(e) => setFiltre(e.target.value)}
                autoComplete="off"
              />
            </div>
          )}
        </div>

        {organisationsCochees.length === 0 ? (
          <p className="orga-sites__vide">
            <i className="fa fa-info-circle" aria-hidden="true" />
            Cochez une organisation pour choisir ses sites.
          </p>
        ) : (
          organisationsCochees.map((organisation) => {
            const sitesOrga = sitesDeOrganisation(sites, organisation.id);
            const visibles = filtrerSites(sitesOrga, filtre);
            const selectionnes = compteSelectionnes(organisation.id);
            const enChargement = organisationsEnChargement.includes(organisation.id);
            const replie = !filtreActif && replies.includes(organisation.id);
            const listeId = `${idPrefix}-liste-${organisation.id}`;
            const titreId = `${idPrefix}-groupe-${organisation.id}`;
            return (
              <section key={organisation.id} className="groupe-sites" aria-labelledby={titreId}>
                <header className="groupe-sites__entete">
                  <button
                    type="button"
                    className="groupe-sites__replier"
                    aria-expanded={!replie}
                    aria-controls={listeId}
                    onClick={() => basculerRepli(organisation.id)}
                  >
                    <i className="fa fa-chevron-down groupe-sites__chevron" aria-hidden="true" />
                    <span id={titreId} className="groupe-sites__titre">
                      {organisation.name}
                    </span>
                  </button>
                  <span className="groupe-sites__compte">
                    {enChargement ? "Chargement…" : libelleCompteSites(selectionnes, sitesOrga.length)}
                    {filtreActif && !enChargement && sitesOrga.length > 0 && (
                      <> · {visibles.length} affiché{visibles.length > 1 ? "s" : ""}</>
                    )}
                  </span>
                  {sitesOrga.length > 1 && (
                    <span className="groupe-sites__actions">
                      <button
                        type="button"
                        className="btn btn-sm btn-link"
                        onClick={() => onDefinirSitesDe(organisation.id, true)}
                        disabled={selectionnes === sitesOrga.length}
                      >
                        Tout sélectionner
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-link"
                        onClick={() => onDefinirSitesDe(organisation.id, false)}
                        disabled={selectionnes === 0}
                      >
                        Tout retirer
                      </button>
                    </span>
                  )}
                </header>
                <div id={listeId} hidden={replie}>
                  {enChargement ? (
                    <ul className="groupe-sites__liste" aria-hidden="true">
                      {[0, 1, 2].map((i) => (
                        <li key={i} className="choix-site choix-site--squelette">
                          <span className="skeleton-bar" style={{ width: `${60 + i * 12}%` }} />
                        </li>
                      ))}
                    </ul>
                  ) : sitesOrga.length === 0 ? (
                    <p className="orga-sites__vide">Aucun site pour cette organisation.</p>
                  ) : visibles.length === 0 ? (
                    <p className="orga-sites__vide">Aucun site ne correspond à « {filtre.trim()} ».</p>
                  ) : (
                    <ul className="groupe-sites__liste">
                      {visibles.map((site) => {
                        const coche = selectionSites.includes(site.id);
                        const id = `${idPrefix}-site-${site.id}`;
                        const commune = site.location && site.location.name;
                        return (
                          <li key={site.id}>
                            <label htmlFor={id} className={`choix-site${coche ? " is-checked" : ""}`}>
                              <input
                                type="checkbox"
                                id={id}
                                name="sites"
                                value={site.id}
                                checked={coche}
                                onChange={() => onBasculerSite(site.id)}
                              />
                              <span className="choix-site__texte">
                                <span className="choix-site__nom">{site.name}</span>
                                {commune && <span className="choix-site__commune">{commune}</span>}
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </section>
            );
          })
        )}
        {erreurs.sites && (
          <small role="alert" className="form-text text-danger">
            {erreurs.sites}
          </small>
        )}
      </div>
    </div>
  );
};

export default OrganisationsEtSites;
